import { useCallback, useMemo, useRef, useState } from 'react';
import { Campaign, User } from '../../../../types';
import { subscribeToUsers } from '../../../../services/users';
import { loadTaskFileContents } from '../../../../services/honeydewResultStorage';
import { campaignRepository } from '../../../../platform/firebase/campaignRepository';
import {
  SyncAlignedCampaignsResult,
  syncAlignedCampaigns,
} from '../../../../services/alignedCampaigns';
import {
  CampaignStatusCounts,
  listTasksForCampaign,
} from '../../../../services/tasks';
import { filterCampaigns } from './filter';
import {
  downloadBlob,
  ExportFormat,
  ExportType,
  exportFilename,
  filterTasksByStatus,
  resolveTaskExpertIds,
  reshapeTaskRowsForExport,
  TaskStatusFilter,
  tasksToCsv,
  tasksToJson,
  toTaskAnswers,
} from '../../../../services/taskExport';
import { getTaskTypeDescriptor } from '../../../../taskTypes/descriptor';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import { createQueueReadController } from '../../../../features/taskQueue/readController';

export interface ExportTasksOptions {
  format: ExportFormat;
  statusFilter: TaskStatusFilter;
  exportType: ExportType;
  /** Task type to scope the export to. Required — exports are per-type. */
  taskTypeId: string;
}

export type CampaignCounts = CampaignStatusCounts;

export interface CampaignsToolViewModel {
  busy: boolean;
  error: string | null;
  syncing: boolean;
  lastSyncResult: SyncAlignedCampaignsResult | null;
  campaigns: Campaign[];
  /** Campaigns passing the current query + task-type filter. */
  filteredCampaigns: Campaign[];
  usersByExpertId: Map<string, User>;
  /** Aggregate per-campaign counts. Loaded lazily as rows enter view. */
  countsByCampaign: Map<string, CampaignCounts>;
  countsLoading: Set<string>;
  exporting: Set<string>;
  query: string;
  setQuery: (q: string) => void;
  taskTypeIds: string[];
  toggleTaskTypeFilter: (id: string) => void;
  clearTaskTypeFilter: () => void;
  initialize: () => void;
  dispose: () => void;
  sync: () => Promise<void>;
  loadCounts: (campaignId: string) => Promise<void>;
  exportTasks: (campaignId: string, options: ExportTasksOptions) => Promise<void>;
}

/**
 * Per-type counts cache key. Kept exported because the campaign detail page
 * (and any future per-type consumer) needs the same canonical key shape when
 * looking up its own counts cache.
 */
export function campaignTypeCountsKey(campaignId: string, taskTypeId: string, domainId?: string | null): string {
  return domainId ? `${campaignId}::${taskTypeId}::${domainId}` : `${campaignId}::${taskTypeId}`;
}

export function useCampaignsToolViewModel(): CampaignsToolViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  // Counts come from the same read-controller seam the queue uses, so
  // there is one data path to campaign status counts (M88).
  const readControllerRef = useRef(createQueueReadController());
  const readController = readControllerRef.current;

  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [lastSyncResult, setLastSyncResult] = useState<SyncAlignedCampaignsResult | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [countsByCampaign, setCountsByCampaign] = useState<Map<string, CampaignCounts>>(new Map());
  const [countsLoading, setCountsLoading] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState<string>('');
  const [taskTypeIds, setTaskTypeIds] = useState<string[]>([]);
  const unsubsRef = useRef<Array<() => void>>([]);

  const toggleTaskTypeFilter = useCallback((id: string) => {
    setTaskTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);
  const clearTaskTypeFilter = useCallback(() => setTaskTypeIds([]), []);

  const dispose = useCallback(() => {
    unsubsRef.current.forEach((u) => u());
    unsubsRef.current = [];
  }, []);

  const initialize = useCallback(() => {
    setBusy(true);
    setError(null);
    dispose();

    let campaignsLoaded = false;
    let usersLoaded = false;
    const maybeDone = () => {
      if (campaignsLoaded && usersLoaded) setBusy(false);
    };

    const unsubCampaigns = campaignRepository.subscribeAll(
      (next) => {
        setCampaigns(next);
        campaignsLoaded = true;
        maybeDone();
      },
      (err) => {
        setError(err.message);
        setBusy(false);
      },
    );
    const unsubUsers = subscribeToUsers(
      (next) => {
        setUsers(next);
        usersLoaded = true;
        maybeDone();
      },
      (err) => {
        setError(err.message);
        setBusy(false);
      },
    );
    unsubsRef.current = [unsubCampaigns, unsubUsers];
  }, [dispose]);

  const usersByExpertId = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) {
      if (u.expertId) m.set(u.expertId, u);
    }
    return m;
  }, [users]);

  const usersByUid = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) m.set(u.uid, u);
    return m;
  }, [users]);

  const usersByUidRef = useRef(usersByUid);
  usersByUidRef.current = usersByUid;

  const sync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    try {
      const result = await syncAlignedCampaigns();
      setLastSyncResult(result);
    } catch (e: any) {
      setError(e?.message || 'Sync failed');
      logGlobalError(e, { context: 'CampaignsTool.sync' });
      throw e;
    } finally {
      setSyncing(false);
    }
  }, []);

  const filteredCampaigns = useMemo(
    () => filterCampaigns(campaigns, { query, taskTypeIds }),
    [campaigns, query, taskTypeIds],
  );

  const loadCounts = useCallback(async (campaignId: string) => {
    setCountsLoading((prev) => {
      const next = new Set(prev);
      next.add(campaignId);
      return next;
    });
    try {
      const aggregate = await readController.countTasksByStatus(campaignId);
      setCountsByCampaign((prev) => {
        const next = new Map(prev);
        next.set(campaignId, aggregate);
        return next;
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load counts');
      logGlobalError(e, {
        context: 'CampaignsTool.loadCounts',
        metadata: { campaignId },
      });
    } finally {
      setCountsLoading((prev) => {
        const next = new Set(prev);
        next.delete(campaignId);
        return next;
      });
    }
  }, []);

  const exportTasks = useCallback(
    async (campaignId: string, { format, statusFilter, exportType, taskTypeId }: ExportTasksOptions) => {
      setExporting((prev) => {
        const next = new Set(prev);
        next.add(campaignId);
        return next;
      });
      setError(null);
      try {
        const rawTasks = await listTasksForCampaign(campaignId);
        const scoped = rawTasks.filter((t) => t.taskType === taskTypeId);
        const withExpert = resolveTaskExpertIds(scoped, usersByUidRef.current);
        const filtered = [];
        for (const task of filterTasksByStatus(withExpert, statusFilter)) {
          filtered.push(await loadTaskFileContents(task));
        }
        const rows = exportType === 'answers' ? toTaskAnswers(filtered) : filtered;
        const runtime = await getTaskTypeDescriptor(taskTypeId).loadRuntime();
        const reshapeResult = runtime.reshapeResultForExport;
        const content =
          format === 'csv'
            ? tasksToCsv(reshapeTaskRowsForExport(rows, reshapeResult))
            : tasksToJson(rows, { reshapeResult });
        const mime =
          format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8';
        downloadBlob(content, exportFilename(campaignId, format, { taskTypeId }), mime);
      } catch (e: any) {
        setError(e?.message || 'Failed to export tasks');
        logGlobalError(e, {
          context: 'CampaignsTool.exportTasks',
          metadata: { campaignId, taskTypeId, format, statusFilter, exportType },
        });
        throw e;
      } finally {
        setExporting((prev) => {
          const next = new Set(prev);
          next.delete(campaignId);
          return next;
        });
      }
    },
    [],
  );

  return {
    busy,
    error,
    syncing,
    lastSyncResult,
    campaigns,
    filteredCampaigns,
    usersByExpertId,
    countsByCampaign,
    countsLoading,
    exporting,
    query,
    setQuery,
    taskTypeIds,
    toggleTaskTypeFilter,
    clearTaskTypeFilter,
    initialize,
    dispose,
    sync,
    loadCounts,
    exportTasks,
  };
}
